import {useSessionControls} from '../session/SessionContext'
import React, {useEffect, useId, useRef, useState} from 'react'
import {useAppSelector} from '../store/hooks'
import {Box, Button, ButtonBase, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Chip, LinearProgress, Paper, Stack} from '@mui/material'
import {TIMER_FIT_MEDIA} from '../theme/feltTheme'
import Chips from './chips/Chips'
import Controls from './controls/Controls'
import Level from './level/Level'
import Break from './break/Break'
import NextLevel from './level/NextLevel'
import isBreakNext from './break/isBreakNext'
import Blind from './blind/Blind'
import Typography from '@mui/material/Typography'
import NextBreak from './break/NextBreak'
import PlaybackOverlay from './PlaybackOverlay'
import {visuallyHidden} from '@mui/utils'
import SynchronizationIndicator from '../session/SynchronizationIndicator'

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement && (
    target.tagName === 'INPUT' ||
  target.tagName === 'TEXTAREA' ||
  target.tagName === 'SELECT' ||
  target.isContentEditable)

function BlindsTimer() {
  const blinds = useAppSelector((schedule) => schedule.blinds)
  const chips = useAppSelector((schedule) => schedule.chips)
  const onBreak = useAppSelector((schedule) => schedule.onBreak)
  const currentBlindNumber = useAppSelector((schedule) => schedule.currentBlindNumber)
  const breaks = useAppSelector((schedule) => schedule.breaks)
  const levelLength = useAppSelector((schedule) => schedule.levelLength)
  const nextBreakNumber = useAppSelector((schedule) => schedule.nextBreakNumber)
  const secondsLeftInCurrentSession = useAppSelector((schedule) => schedule.secondsLeftInCurrentSession)
  const {toggle: toggleTimer, reset: resetLevel, skip: skipLevel, canControl, status, syncStatus, session} = useSessionControls()
  const [pending, setPending] = useState<{action: 'Skip' | 'Reset'; segment: string} | null>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const confirmationTitleId = useId()
  const confirmationDescriptionId = useId()
  const segment = `${currentBlindNumber}:${onBreak}:${nextBreakNumber}`
  const confirmationOpen = pending !== null && pending.segment === segment && canControl
  useEffect(() => {
    if (pending && (pending.segment !== segment || !canControl)) setPending(null)
  }, [segment, canControl, pending])
  const isLastLevel = currentBlindNumber >= blinds.length - 1
  const breakIsNext = !onBreak && isBreakNext(breaks, nextBreakNumber, currentBlindNumber)
  const currentBlind = blinds[currentBlindNumber]
  const nextBlind = blinds[currentBlindNumber + 1] ?? null
  const clockIsRunning = useAppSelector(schedule => schedule.timerPlaying ?? false)
  const hasStarted = useAppSelector(schedule => schedule.hasStarted)
  const countdownCompleteCallback = () => {}
  const renderBreak = () => (
    <Break countdownCompleteCallback={countdownCompleteCallback}
      clockIsRunning={clockIsRunning}
      length={breaks[nextBreakNumber].length}
      secondsLeftInCurrentSession={secondsLeftInCurrentSession}
    />
  )
  const renderCurrentLevel = () => (
    <Level blind={currentBlind}
      secondsLeftInCurrentSession={secondsLeftInCurrentSession}
      countdownCompleteCallback={countdownCompleteCallback}
      level={currentBlindNumber + 1}
      length={levelLength}
      lastLevel={isLastLevel}
      onBreak={onBreak}
      clockIsRunning={clockIsRunning}
      isCurrentLevel={true}/>
  )
  const renderNextLevel = () => (
    <NextLevel blind={nextBlind}
      level={currentBlindNumber + 2}
      length={levelLength}
    />
  )
  const renderNextLevelAsBreak = () => (
    <NextLevel
      onBreak={true}
      length={breaks[nextBreakNumber].length}
    />
  )

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (confirmationOpen || !canControl || isLastLevel || event.repeat || isTypingTarget(event.target) ||
        (event.target instanceof Element && event.target.closest('button, a, [role="button"]'))) {
        return
      }
      switch (event.code) {
        case 'Space':
          event.preventDefault()
          toggleTimer()
          break
        case 'ArrowLeft':
          event.preventDefault()
          setPending({action: 'Reset', segment})
          break
        case 'ArrowRight':
          event.preventDefault()
          setPending({action: 'Skip', segment})
          break
        default:
          break
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [canControl, isLastLevel, toggleTimer, confirmationOpen, segment])

  const renderCountdown = () => <PlaybackOverlay playing={clockIsRunning} hasStarted={hasStarted} suddenDeath={isLastLevel}
    observationKey={status === 'Recovering' ? 'recovering' : session?.sessionId ?? 'standalone'}>
    {onBreak ? renderBreak() : renderCurrentLevel()}
  </PlaybackOverlay>
  const duration = (onBreak ? breaks[nextBreakNumber].length : levelLength) * 60
  const remainingPercent = Math.max(0, Math.min(100, secondsLeftInCurrentSession / duration * 100))
  return <Stack spacing={1} sx={{width: '100%', maxWidth: 1400, mx: 'auto', [TIMER_FIT_MEDIA]: {maxWidth: 'none', height: '100%', display: 'grid'}, gridTemplateRows: `minmax(0, 1fr) auto ${!isLastLevel && canControl ? 'auto ' : ''}auto`, '--timer-blind-font': {tablet: '4rem', xxl: '6rem', xxxl: '8rem'}, '--timer-chip-size': {xs: '5rem', xxl: '8rem', xxxl: '10rem'}, '--timer-font-cap': {tablet: '44rem', xxl: '56rem', xxxl: '96rem'}, '--timer-label-font': {xxl: '1rem', xxxl: '1.25rem'}}}>
    <Paper sx={{p: {xs: 1.5, sm: 2}, display: 'flex', flexDirection: 'column', minHeight: 0}}>
      <Stack direction="row" sx={{justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1, position: 'relative', zIndex: 1}}>
        {onBreak ? <Typography>Break:</Typography> : <Typography>Round: {currentBlindNumber + 1}</Typography>}
        <Stack direction="row" spacing={1} role="group" aria-label="Timer mode and synchronization" sx={{alignItems: 'center', flexWrap: 'wrap'}}>
          <Chip size="small" label={status}/>
          {session ? <SynchronizationIndicator/> : <Chip size="small" label={status === 'Standalone' ? 'Local timer' : syncStatus}/>}
          {session && <Box component="output" role="status" aria-label="Hosting synchronization status" sx={visuallyHidden}>{syncStatus}</Box>}
        </Stack>
        <Typography component="span" role="status" aria-label="Playback status" sx={visuallyHidden}>{isLastLevel ? 'Final level' : clockIsRunning ? 'Running' : 'Paused'}</Typography>
      </Stack>
      <Box sx={{textAlign: 'center', position: 'relative', py: 1, flex: 1, minHeight: 0, [TIMER_FIT_MEDIA]: {py: 0}}}>
        {canControl && !isLastLevel ? <ButtonBase component="div" aria-label={clockIsRunning ? 'Pause tournament timer' : 'Play tournament timer'} onClick={toggleTimer}
          sx={{display: 'block', width: '100%', [TIMER_FIT_MEDIA]: {height: '100%'}, borderRadius: 1, '&.Mui-focusVisible': {outline: '3px solid', outlineColor: 'primary.main', outlineOffset: 4}}}>
          {renderCountdown()}
        </ButtonBase> : renderCountdown()}
      </Box>
      {!isLastLevel && <LinearProgress variant="determinate" value={remainingPercent} aria-label="Round time remaining" sx={{height: 7, borderRadius: 4, mb: 1.5}}/>}
      {onBreak ? <Typography sx={{position: 'relative', zIndex: 1, textAlign: 'center', fontSize: '2rem', fontWeight: 700}}>Break</Typography> : <Blind blind={currentBlind}/>}
    </Paper>
    <Paper sx={{p: {xs: 1.5, sm: 2}}}>
      <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} sx={{justifyContent: 'space-between', alignItems: {sm: 'center'}}}>
        <Box id="blindsTimerBreakOrNextLevel">
          {breakIsNext && renderNextLevelAsBreak()}
          {!isLastLevel && !breakIsNext && renderNextLevel()}
          {isLastLevel && <Typography color="text.secondary">Final round</Typography>}
        </Box>
        <NextBreak/>
      </Stack>
    </Paper>
    {!isLastLevel && canControl && <Box id="blindsTimerControls"><Controls controlText={clockIsRunning ? 'Pause' : 'Play'} resetLevel={resetLevel} confirmReset={() => setPending({action: 'Reset', segment})} skipLevel={() => setPending({action: 'Skip', segment})} toggleTimer={toggleTimer} disabled={!canControl}/></Box>}
    <Dialog open={confirmationOpen} onClose={() => setPending(null)} aria-labelledby={confirmationTitleId} aria-describedby={confirmationDescriptionId} slotProps={{transition: {onEntered: () => cancelRef.current?.focus()}}}>
      <DialogTitle id={confirmationTitleId}>{pending?.action} {onBreak ? 'break' : `round ${currentBlindNumber + 1}`}?</DialogTitle>
      <DialogContent><DialogContentText id={confirmationDescriptionId}>The timer continues while you decide.</DialogContentText></DialogContent>
      <DialogActions>
        <Button ref={cancelRef} onClick={() => setPending(null)}>Cancel</Button>
        <Button onClick={() => {
          if (confirmationOpen) {
            const target = {levelIndex: currentBlindNumber, breakIndex: nextBreakNumber, onBreak}
            if (pending.action === 'Skip') skipLevel(target)
            else resetLevel(target)
          }
          setPending(null)
        }}>{pending?.action}</Button>
      </DialogActions>
    </Dialog>
    <Paper id="blindsTimerChips" sx={{p: 1.5}}>
      <Typography variant="overline" color="text.secondary" component="h2" sx={{mb: 1, letterSpacing: 2, fontSize: 'var(--timer-label-font, 0.75rem)'}}>Chip values</Typography>
      <Chips chips={chips}/>
    </Paper>
  </Stack>
}

export default BlindsTimer
